/**
 * Maps each dashboard filter key to its Stop-Loss Claims List filter key. The
 * claims dashboard and list use identical singular keys today, but the mapping
 * is kept explicit to mirror buildPolicyDrillLink and to leave room for any
 * future dashboard-plural → list-singular divergence.
 */
const CARRIED_FILTERS: ReadonlyArray<readonly [dashboardKey: string, listKey: string]> = [
  ["client_id_ref", "client_id_ref"],
  ["vendor_id_ref", "vendor_id_ref"],
];

/** True when a filter value carries at least one selection. */
const hasValue = (value: string | string[] | undefined): boolean => {
  if (value === undefined) return false;
  return Array.isArray(value) ? value.length > 0 : value !== "";
};

/** Extra filter entries a specific card contributes (e.g. a claim-status predicate). */
type ExtraFilter = Record<string, string | number | boolean | string[]>;

/**
 * Builds a Stop-Loss Claims List drill-through URL for a claims-dashboard card.
 *
 * Carries over the dashboard's current Client / Carrier filters (including
 * multi-select arrays) so the destination list reflects the same population the
 * metric was computed over, and merges any card-specific `extra` predicate —
 * e.g. the Pended card passes `{ claim_status: "pended" }`, the Open card
 * `{ claim_status: ["new", "in_process"] }`.
 *
 * @param filters - The dashboard's currently-active filter object (parsed from
 *   the `filter` URL search param).
 * @param extra - Card-specific filter entries merged into the destination filter.
 * @returns A path like `/stoploss-claims?filter={"claim_status":"pended",...}`.
 */
export const buildClaimDrillLink = (filters: Record<string, string | string[]>, extra: ExtraFilter = {}): string => {
  const drillFilter: Record<string, string | number | boolean | string[]> = { ...extra };
  for (const [dashboardKey, listKey] of CARRIED_FILTERS) {
    const value = filters[dashboardKey];
    if (hasValue(value)) drillFilter[listKey] = value;
  }
  // Encode the filter — reserved chars in ids (#, &, +, space) would otherwise
  // corrupt the query string, and `#` is especially dangerous under HashRouter.
  return `/stoploss-claims?filter=${encodeURIComponent(JSON.stringify(drillFilter))}`;
};
